import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, useAuth } from "../App";
import { sounds, burst, addXP, addCombo, getDaysUntil } from "../hooks/useDMEngine";
import { DailyPriorityModal, ReflectionModal } from "../components/DailyPriorities";
import usePullToRefresh from "../hooks/usePullToRefresh";
import useIsMobile from "../hooks/useIsMobile";
import useShortcuts from "../hooks/useShortcuts";
import MobileSkeleton from "../components/MobileSkeleton";
import MobileStoriesBar from "../components/MobileStoriesBar";
import MobileStoryViewer from "../components/MobileStoryViewer";

import MyDayHeader from "../components/MyDayHeader";
import CaptureRow from "../components/CaptureRow";
import JotsBoard from "../components/JotsBoard";
import UnifiedTodayList from "../components/UnifiedTodayList";
import MyBriefsSection from "../components/MyBriefsSection";
import CollapsedSection from "../components/CollapsedSection";
import TodayRow from "../components/TodayRow";
import AvatarChip from "../components/ui/avatar-chip";

/**
 * /me — the primary workspace.
 * Phase A of the /me rearchitecture:
 *  - Apple-minimal unified Today list (priorities + overdue/today tasks + briefs due soon)
 *  - My briefs always-visible section
 *  - Awaiting me + This week as collapsed sections
 *  - Capture row with + New task / + New brief buttons (Phase B wires the jot path)
 *  - Desktop + mobile share the layout (mobile simplifies with stickier capture + shorter buttons)
 *
 * Preserved from prior implementation:
 *  - Optimistic task complete with 5s Undo (Week 2 Day 1)
 *  - DailyPriorityModal blocking on first open of day
 *  - ReflectionModal trigger (handled globally by AppShell)
 *  - Keyboard nav (j/k/c/Enter) on Today list
 *  - Pull-to-refresh
 *  - Mobile stories bar + story viewer (social/pulse surface stays)
 */
// "Skip for today" on the daily-priorities prompt: suppress re-prompts for the
// rest of the calendar day so it never nags once dismissed.
const prioritySkipKey = () => `dm_pri_skip_${new Date().toISOString().slice(0, 10)}`;
const prioritiesSkippedToday = () => { try { return localStorage.getItem(prioritySkipKey()) === "1"; } catch { return false; } };
const skipPrioritiesToday = () => { try { localStorage.setItem(prioritySkipKey(), "1"); } catch {} };

export default function MyDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [briefs, setBriefs] = useState([]);
  const [jots, setJots] = useState([]);
  // (jotsFromYesterday removed — open jots auto-roll forward; see server /me/workspace.)
  const [teamPulse, setTeamPulse] = useState(null);
  const [orgPulse, setOrgPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soundsOn] = useState(() => localStorage.getItem("dm_sounds") !== "off");
  const [completingTask, setCompletingTask] = useState(null);
  const [dailyPriorities, setDailyPriorities] = useState(null);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [storyUser, setStoryUser] = useState(null);
  const isMobile = useIsMobile();

  const sfx = useCallback((fn) => { if (soundsOn) fn(); }, [soundsOn]);

  // ─────────── Data ───────────
  // Primary path: single consolidated /me/workspace call (Phase B). Fallback to the
  // legacy parallel fetch if workspace endpoint isn't available yet (old backend).
  const loadData = useCallback(async () => {
    try {
      const ws = await api.get("/me/workspace").catch(() => null);
      if (ws?.data) {
        const w = ws.data;
        setData({
          my_tasks: w.my_tasks || [],
          my_projects: w.my_projects || [],
          my_briefs: w.my_briefs || [],
          review_tasks: w.review_tasks || [],
          review_projects: w.review_projects || [],
        });
        setBriefs(w.my_briefs || []);
        setJots([...(w.jots_today || []), ...(w.jots_done_today || [])]);
        setTeamPulse(w.team_pulse || null);
        setOrgPulse(w.org_pulse || null);
        // Daily priorities — the workspace endpoint returns the raw doc; reshape
        setDailyPriorities(w.priorities_today ? { today: w.priorities_today } : null);
        // No auto-pop: the daily-priorities prompt no longer interrupts on load.
        setLoading(false);
        return;
      }
      // Legacy fallback
      const [me, briefsRes, dpRes] = await Promise.all([
        api.get("/me/dashboard"),
        api.get("/bizdev/briefs").catch(() => ({ data: [] })),
        api.get("/me/daily-priorities").catch(() => ({ data: null })),
      ]);
      setData(me.data);
      setBriefs(Array.isArray(briefsRes.data) ? briefsRes.data : []);
      setDailyPriorities(dpRes.data);
      // No auto-pop (legacy path) — the priorities prompt never interrupts load.
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  usePullToRefresh(loadData);

  // Refetch when the brain-dump sheet saves jots from anywhere in the app
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => loadData();
    window.addEventListener("my-day-jots-updated", handler);
    return () => window.removeEventListener("my-day-jots-updated", handler);
  }, [loadData]);

  // ─────────── Derived ───────────
  // User-local (IST) date so task overdue/today bucketing matches the server.
  const todayStr = useMemo(() => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10), []);

  const d = data || {};
  const myTasks = useMemo(() => d.my_tasks || [], [d.my_tasks]);
  const myProjects = useMemo(() => d.my_projects || [], [d.my_projects]);
  const myBriefs = useMemo(() => d.my_briefs || [], [d.my_briefs]);
  const reviewTasks = useMemo(() => d.review_tasks || [], [d.review_tasks]);
  const reviewProjects = useMemo(() => d.review_projects || [], [d.review_projects]);

  // Briefs with pitch in the next 48h — surfaced in Today
  const briefsDueSoon = useMemo(() => {
    const src = myBriefs && myBriefs.length ? myBriefs : briefs;
    return (src || []).filter((b) => {
      if (!b || !b.pitch_date) return false;
      const pd = b.pitch_date.slice(0, 10);
      const diff = daysDiff(todayStr, pd);
      return diff >= 0 && diff <= 1;
    });
  }, [myBriefs, briefs, todayStr]);

  // Overdue tasks (for carryover-style glance)
  const overdueCount = useMemo(() => {
    return myTasks.filter((t) => {
      if (!t.due_date || t.status === "Done" || t.status === "Completed") return false;
      return t.due_date.slice(0, 10) < todayStr;
    }).length;
  }, [myTasks, todayStr]);

  // Upcoming tasks this week (excluding today)
  const upcomingThisWeek = useMemo(() => {
    return myTasks.filter((t) => {
      if (!t.due_date || t.status === "Done" || t.status === "Completed") return false;
      const dd = t.due_date.slice(0, 10);
      if (dd <= todayStr) return false;
      return getDaysUntil(dd) <= 7;
    });
  }, [myTasks, todayStr]);

  // Upcoming briefs (next 7 days, excluding today/tomorrow which surface in Today)
  const upcomingBriefs = useMemo(() => {
    const src = myBriefs && myBriefs.length ? myBriefs : briefs;
    return (src || []).filter((b) => {
      if (!b || !b.pitch_date) return false;
      const diff = daysDiff(todayStr, b.pitch_date.slice(0, 10));
      return diff >= 2 && diff <= 7;
    });
  }, [myBriefs, briefs, todayStr]);

  // Active projects
  const activeProjects = useMemo(() => {
    return myProjects.filter((p) => p.status !== "Done" && p.status !== "Completed");
  }, [myProjects]);

  // Priorities (array shape: priorities.today[0..2] or priorities.today = [slots])
  const priorityItems = useMemo(() => {
    const today = dailyPriorities && (dailyPriorities.today || null);
    if (!today) return [null, null, null];
    const slots = Array.isArray(today) ? today : (today.priorities || today.slots || []);
    return [0, 1, 2].map((i) => slots[i] || null);
  }, [dailyPriorities]);

  // ─────────── Mutations ───────────
  // Optimistic task complete with 5s Undo (preserved from Week 2 Day 1)
  const completeTask = useCallback(async (taskId, burstEvent) => {
    const task = myTasks.find((t) => t.task_id === taskId);
    if (!task) return;
    const prevStatus = task.status;

    const snapshot = {
      my_tasks: data?.my_tasks ? [...data.my_tasks] : [],
      review_tasks: data?.review_tasks ? [...data.review_tasks] : [],
    };

    // Optimistic UI
    setData((prev) => prev ? ({
      ...prev,
      my_tasks: (prev.my_tasks || []).filter((t) => t.task_id !== taskId),
      review_tasks: (prev.review_tasks || []).filter((t) => t.task_id !== taskId),
    }) : prev);

    sfx(sounds.power);
    addXP(15);
    addCombo();
    if (burstEvent?.clientX) burst(burstEvent.clientX, burstEvent.clientY, "#22c55e", 12);
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(10);

    setCompletingTask(taskId);
    let erred = false;
    try {
      await api.put(`/tasks/${taskId}`, { status: "Done" });
    } catch (err) {
      erred = true;
      console.error("Complete failed:", err);
      setData((prev) => prev ? ({
        ...prev,
        my_tasks: snapshot.my_tasks,
        review_tasks: snapshot.review_tasks,
      }) : prev);
      toast.error("Couldn't complete — reverted");
    } finally {
      setCompletingTask(null);
    }
    if (erred) return;

    toast.success(`Done: ${(task.title || "Task").slice(0, 40)}${task.title?.length > 40 ? "…" : ""}`, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: async () => {
          setData((prev) => prev ? ({
            ...prev,
            my_tasks: snapshot.my_tasks,
            review_tasks: snapshot.review_tasks,
          }) : prev);
          if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
          try {
            await api.put(`/tasks/${taskId}`, { status: prevStatus || "In Progress" });
          } catch (e) {
            console.error("Undo failed:", e);
            toast.error("Undo failed — refresh to see latest");
          }
        },
      },
    });
  }, [myTasks, data, sfx]);

  // ─────────── Jot mutations ───────────
  // bucket = effort type/lane: "checkin" | "deep" | "ops". today=true drops the
  // new jot straight onto the day-plan; the capture bar leaves it in the backlog.
  const createJot = useCallback(async (text, bucket = "checkin", today = false) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return;
    // Optimistic: add to top of active jots with a temp id
    const tempId = `jot_tmp_${Date.now()}`;
    const optimistic = {
      jot_id: tempId,
      text: trimmed,
      done: false,
      bucket,
      today,
      deep_hours: 1,
      ops_minutes: 15,
      pinned_start: null,
      order: -1, // sorts above existing lane cards until the real row arrives
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setJots((prev) => [optimistic, ...prev]);
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(5);
    try {
      const res = await api.post("/me/jots", { text: trimmed, bucket, today });
      const real = res.data;
      // Replace the optimistic row
      setJots((prev) => prev.map((j) => j.jot_id === tempId ? real : j));
    } catch (err) {
      console.error("Jot create failed:", err);
      // Rollback
      setJots((prev) => prev.filter((j) => j.jot_id !== tempId));
      toast.error("Couldn't save — try again");
      throw err;
    }
  }, []);

  // Generic optimistic field patch for a single jot (today, bucket, deep_hours,
  // ops_minutes, pinned_start, …). The day-planner routes all its non-done,
  // non-text edits through here. Pass pinned_start: -1 to clear a pin (auto).
  const patchJot = useCallback(async (jotId, fields) => {
    const snapshot = jots;
    const local = { ...fields };
    if (local.pinned_start === -1) local.pinned_start = null;
    if (local.today === false) { local.planned_date = null; local.pinned_start = null; }
    setJots((prev) => prev.map((j) => (j.jot_id === jotId ? { ...j, ...local } : j)));
    try {
      const res = await api.put(`/me/jots/${jotId}`, fields);
      setJots((prev) => prev.map((j) => (j.jot_id === jotId ? res.data : j)));
    } catch (err) {
      console.error("Jot patch failed:", err);
      setJots(snapshot);
      toast.error("Couldn't save — reverted");
    }
  }, [jots]);

  // Same patch applied to several jots at once (e.g. pinning a whole batch
  // block to a start time). One optimistic update, parallel PUTs.
  const patchJots = useCallback(async (jotIds, fields) => {
    if (!Array.isArray(jotIds) || !jotIds.length) return;
    const snapshot = jots;
    const idset = new Set(jotIds);
    const local = { ...fields };
    if (local.pinned_start === -1) local.pinned_start = null;
    if (local.today === false) { local.planned_date = null; local.pinned_start = null; }
    setJots((prev) => prev.map((j) => (idset.has(j.jot_id) ? { ...j, ...local } : j)));
    try {
      await Promise.all(jotIds.map((id) => api.put(`/me/jots/${id}`, fields)));
    } catch (err) {
      console.error("Bulk jot patch failed:", err);
      setJots(snapshot);
      toast.error("Couldn't save — reverted");
    }
  }, [jots]);

  // Persist a new ordering for a set of jot_ids (backlog drag-reorder). Order is
  // group-relative; we sort backlog and plan independently, so collisions across
  // the two are harmless.
  const reorderJotIds = useCallback(async (orderedIds) => {
    if (!Array.isArray(orderedIds) || !orderedIds.length) return;
    const snapshot = jots;
    const pos = new Map(orderedIds.map((id, i) => [id, i]));
    setJots((prev) => prev.map((j) => (pos.has(j.jot_id) ? { ...j, order: pos.get(j.jot_id) } : j)));
    try {
      await api.post("/me/jots/reorder", { jot_ids: orderedIds });
    } catch (err) {
      console.error("Reorder failed:", err);
      setJots(snapshot);
      toast.error("Couldn't reorder — reverted");
    }
  }, [jots]);

  const toggleJot = useCallback(async (jotId, nextDone) => {
    const snapshot = jots;
    setJots((prev) => prev.map((j) => j.jot_id === jotId ? { ...j, done: nextDone, done_at: nextDone ? new Date().toISOString() : null } : j));
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(6);
    try {
      await api.put(`/me/jots/${jotId}`, { done: nextDone });
    } catch (err) {
      console.error("Jot toggle failed:", err);
      setJots(snapshot);
      toast.error("Couldn't update — reverted");
    }
  }, [jots]);

  const deleteJot = useCallback(async (jotId) => {
    const snapshot = jots;
    const target = jots.find((j) => j.jot_id === jotId);
    setJots((prev) => prev.filter((j) => j.jot_id !== jotId));
    toast(`Deleted jot: ${(target?.text || "").slice(0, 40)}`, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => setJots(snapshot),
      },
    });
    try {
      await api.delete(`/me/jots/${jotId}`);
    } catch (err) {
      console.error("Jot delete failed:", err);
      setJots(snapshot);
      toast.error("Delete failed — restored");
    }
  }, [jots]);

  // Reorder jots within today via backend batch endpoint
  const reorderJots = useCallback(async (orderedJotIds) => {
    if (!Array.isArray(orderedJotIds) || orderedJotIds.length === 0) return;
    // Optimistic local reorder (active jots only, done-today stays appended)
    const snapshot = jots;
    const idToJot = new Map(jots.map((j) => [j.jot_id, j]));
    const doneOnes = jots.filter((j) => j.done);
    const newActive = orderedJotIds.map((id) => idToJot.get(id)).filter(Boolean);
    setJots([...newActive, ...doneOnes]);
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
    try {
      await api.post("/me/jots/reorder", { jot_ids: orderedJotIds });
    } catch (err) {
      console.error("Reorder failed:", err);
      setJots(snapshot);
      toast.error("Couldn't save order — reverted");
    }
  }, [jots]);

  // Drag-and-drop / ‹ › move on the 3-column board. `orderedJotIds` is the
  // target lane's full active ordering after the move; every listed jot is
  // re-homed into `bucket` and re-ordered 0..n in one backend write.
  const reorderColumn = useCallback(async (bucket, orderedJotIds) => {
    if (!bucket || !Array.isArray(orderedJotIds)) return;
    const snapshot = jots;
    const orderMap = new Map(orderedJotIds.map((id, i) => [id, i]));
    setJots((prev) => prev.map((j) => {
      if (!orderMap.has(j.jot_id)) return j;
      return { ...j, bucket, order: orderMap.get(j.jot_id) };
    }));
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
    try {
      await api.post("/me/jots/reorder", { jot_ids: orderedJotIds, bucket });
    } catch (err) {
      console.error("Column reorder failed:", err);
      setJots(snapshot);
      toast.error("Couldn't move — reverted");
    }
  }, [jots]);

  // Inline rename a jot
  const editJotText = useCallback(async (jotId, newText) => {
    const trimmed = (newText || "").trim();
    if (!trimmed) return;
    const snapshot = jots;
    setJots((prev) => prev.map((j) => j.jot_id === jotId ? { ...j, text: trimmed } : j));
    try {
      await api.put(`/me/jots/${jotId}`, { text: trimmed });
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(6);
    } catch (err) {
      console.error("Jot rename failed:", err);
      setJots(snapshot);
      toast.error("Couldn't save change — reverted");
      throw err;
    }
  }, [jots]);

  // Inline rename a task from /me. Optimistic update, rolls back on failure.
  const editTaskTitle = useCallback(async (taskId, newTitle) => {
    const trimmed = (newTitle || "").trim();
    if (!trimmed) return;
    const snapshot = {
      my_tasks: data?.my_tasks ? [...data.my_tasks] : [],
      review_tasks: data?.review_tasks ? [...data.review_tasks] : [],
    };
    setData((prev) => prev ? ({
      ...prev,
      my_tasks: (prev.my_tasks || []).map((t) => t.task_id === taskId ? { ...t, title: trimmed } : t),
      review_tasks: (prev.review_tasks || []).map((t) => t.task_id === taskId ? { ...t, title: trimmed } : t),
    }) : prev);
    try {
      await api.put(`/tasks/${taskId}`, { title: trimmed });
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(6);
    } catch (err) {
      console.error("Task rename failed:", err);
      setData((prev) => prev ? ({
        ...prev,
        my_tasks: snapshot.my_tasks,
        review_tasks: snapshot.review_tasks,
      }) : prev);
      toast.error("Couldn't rename — reverted");
      throw err;
    }
  }, [data]);

  // Promote jot → Task or Brief. Navigates to the full create form with the
  // title pre-filled and the source jot_id threaded as `from_jot_id`. The
  // create endpoint soft-deletes the jot atomically with the new entity, so
  // the jot disappears from the today list once the task/brief is saved.
  // (bug_cc946f51b211 — "Once a Jot is converted to a task, it should get
  //  deleted automatically".) If the user cancels the create form, the jot
  // stays — only a successful create triggers the delete.
  const promoteJot = useCallback(async (jot, target) => {
    if (!jot || !jot.jot_id) return;
    try {
      // Fire-and-forget promote marker; don't block navigation on it.
      api.post(`/me/jots/${jot.jot_id}/promote`, { to: target }).catch((e) => {
        console.warn("Jot promote marker failed:", e);
      });
    } catch (_) { /* swallow */ }
    const encodedTitle = encodeURIComponent(jot.text || "");
    const encodedJotId = encodeURIComponent(jot.jot_id);
    if (target === "task") {
      navigate(`/tasks/new?title=${encodedTitle}&from_jot_id=${encodedJotId}`);
    } else if (target === "brief") {
      navigate(`/bizdev/briefs/new?title=${encodedTitle}&from_jot_id=${encodedJotId}`);
    }
  }, [navigate]);


  // Delete a task inline from /me with 5s undo (matches the Week 2 pattern)
  const deleteTask = useCallback(async (taskId, task) => {
    const snapshot = {
      my_tasks: data?.my_tasks ? [...data.my_tasks] : [],
      review_tasks: data?.review_tasks ? [...data.review_tasks] : [],
    };
    setData((prev) => prev ? ({
      ...prev,
      my_tasks: (prev.my_tasks || []).filter((t) => t.task_id !== taskId),
      review_tasks: (prev.review_tasks || []).filter((t) => t.task_id !== taskId),
    }) : prev);
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(10);

    let undone = false;
    toast(`Deleted: ${(task?.title || "task").slice(0, 40)}`, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => {
          undone = true;
          setData((prev) => prev ? ({
            ...prev,
            my_tasks: snapshot.my_tasks,
            review_tasks: snapshot.review_tasks,
          }) : prev);
        },
      },
      onAutoClose: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/tasks/${taskId}`); }
          catch (e) {
            console.error("Delete failed:", e);
            setData((prev) => prev ? ({
              ...prev,
              my_tasks: snapshot.my_tasks,
              review_tasks: snapshot.review_tasks,
            }) : prev);
            toast.error("Delete failed — restored");
          }
        }, 50);
      },
      onDismiss: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/tasks/${taskId}`); }
          catch (e) {
            setData((prev) => prev ? ({
              ...prev,
              my_tasks: snapshot.my_tasks,
              review_tasks: snapshot.review_tasks,
            }) : prev);
            toast.error("Delete failed — restored");
          }
        }, 50);
      },
    });
  }, [data]);

  // Priority complete toggle
  const togglePriority = useCallback(async (idx, nextCompleted) => {
    if (!dailyPriorities) return;
    // Optimistic: update local state
    setDailyPriorities((prev) => {
      if (!prev || !prev.today) return prev;
      const today = Array.isArray(prev.today) ? [...prev.today] : { ...prev.today };
      const slots = Array.isArray(today) ? today : (today.priorities || today.slots || []);
      if (slots[idx]) {
        slots[idx] = { ...slots[idx], completed: nextCompleted };
      }
      return Array.isArray(prev.today) ? { ...prev, today: today } : { ...prev, today };
    });
    try {
      await api.put(`/me/daily-priorities/${idx}/complete`, { completed: nextCompleted });
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
    } catch (e) {
      console.error("Priority toggle failed:", e);
      loadData();
    }
  }, [dailyPriorities, loadData]);

  // ─────────── Keyboard nav (desktop only, auto-disabled on touch) ───────────
  const [cursor, setCursor] = useState(-1);
  const cursorRef = useRef(null);
  const focusTasksFlat = useMemo(() => {
    const overdue = myTasks.filter((t) => t.due_date && t.status !== "Done" && t.status !== "Completed" && t.due_date.slice(0, 10) < todayStr);
    const today = myTasks.filter((t) => t.due_date && t.status !== "Done" && t.status !== "Completed" && t.due_date.slice(0, 10) === todayStr);
    return [...overdue, ...today];
  }, [myTasks, todayStr]);

  const clampCursor = useCallback((i) => {
    if (!focusTasksFlat.length) return -1;
    return ((i % focusTasksFlat.length) + focusTasksFlat.length) % focusTasksFlat.length;
  }, [focusTasksFlat.length]);

  useShortcuts([
    { keys: "j", description: "Next task", group: "My Day",
      handler: () => setCursor((c) => clampCursor(c + 1)) },
    { keys: "k", description: "Previous task", group: "My Day",
      handler: () => setCursor((c) => clampCursor(c - 1)) },
    { keys: "enter", description: "Open task", group: "My Day",
      handler: () => {
        const t = focusTasksFlat[cursor];
        if (t) { sfx(sounds.blip); navigate(`/tasks/${t.task_id}`); addXP(5); }
      } },
    { keys: "c", description: "Complete task", group: "My Day",
      handler: () => {
        const t = focusTasksFlat[cursor];
        if (t) completeTask(t.task_id, { clientX: 0, clientY: 0 });
      } },
    { keys: "escape", description: "Clear selection",
      handler: () => setCursor(-1) },
    { keys: "n", description: "Brain dump", group: "My Day",
      handler: () => { if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("open-brain-dump")); } },
  ], { enabled: !isMobile && !loading });

  useEffect(() => {
    if (cursor >= focusTasksFlat.length) setCursor(focusTasksFlat.length - 1);
  }, [focusTasksFlat.length, cursor]);

  // ─────────── Render ───────────
  if (loading) return <MobileSkeleton type="dashboard" />;

  const userName = user?.full_name || user?.name || user?.email?.split("@")[0] || "there";
  const isManager = (user?.role || "").toLowerCase() === "admin"
    || (user?.role || "").toLowerCase() === "srmanager"
    || (reviewTasks.length + reviewProjects.length) > 0;

  return (
    <div
      className="dm-page-container"
      style={{
        maxWidth: 780,
        margin: "0 auto",
        padding: isMobile ? "12px 16px 100px" : "40px 32px 120px",
        fontFamily: "var(--font-sans)",
      }}
    >
      {showPriorityModal && (
        <DailyPriorityModal
          data={dailyPriorities}
          onClose={() => { skipPrioritiesToday(); setShowPriorityModal(false); }}
          onSkip={() => { skipPrioritiesToday(); setShowPriorityModal(false); }}
          onSave={() => { setShowPriorityModal(false); loadData(); }}
        />
      )}

      {/* Mobile stories bar — kept from existing /me for the pulse/social surface */}
      {isMobile && (
        <div style={{ margin: "0 -16px 12px" }}>
          <MobileStoriesBar
            onStoryTap={(u) => setStoryUser(u)}
            onProfileTap={() => window.dispatchEvent(new CustomEvent("openProfile"))}
          />
        </div>
      )}

      {/* Header */}
      <MyDayHeader userName={userName} priorities={priorityItems} />

      {/* Capture row — bar + New task + New brief. New jots land in Check-ins. */}
      <CaptureRow onJotCreate={createJot} />

      {/* Carry-over banner removed — open jots now auto-roll forward. Any
          previously-hidden jots (stranded past the old today/yesterday window)
          come back automatically on the next load. */}

      {/* Jots — backlog + 9a–9p day-planner */}
      <SectionHead
        title="Jots"
        count={(() => { const t = jots.filter((j) => j.today && !j.done).length, b = jots.filter((j) => !j.today && !j.done).length; return `${t} planned · ${b} in backlog`; })()}
        rightHint="drag from backlog onto the day"
      />
      <div style={{ marginBottom: 22 }}>
        <JotsBoard
          jots={jots}
          onCreateJot={createJot}
          onToggleJot={toggleJot}
          onDeleteJot={deleteJot}
          onPromoteJot={promoteJot}
          onEditJotText={editJotText}
          onPatchJot={patchJot}
          onPatchJots={patchJots}
          onReorder={reorderJotIds}
        />
      </div>

      {/* Today — unified flat list (priorities + tasks + briefs; jots live above) */}
      <SectionHead
        title="Today"
        count={describeTodayCount(focusTasksFlat.length, priorityItems, briefsDueSoon.length)}
        rightHint={overdueCount > 0 ? `${overdueCount} overdue` : null}
      />
      <div>
        <UnifiedTodayList
          priorities={priorityItems}
          tasks={myTasks}
          briefsDueSoon={briefsDueSoon}
          jots={[]}
          todayStr={todayStr}
          onNavigate={(p) => { sfx(sounds.blip); navigate(p); addXP(5); }}
          onToggleTask={(id, next) => next && completeTask(id, { clientX: 0, clientY: 0 })}
          onTogglePriority={togglePriority}
          onToggleJot={toggleJot}
          onPromoteJot={promoteJot}
          onDeleteJot={deleteJot}
          onDeleteTask={deleteTask}
          onReorderJots={reorderJots}
          onEditJotText={editJotText}
          onEditTaskTitle={editTaskTitle}
        />
      </div>

      {/* My briefs — always visible */}
      <MyBriefsSection
        briefs={myBriefs.length ? myBriefs : briefs}
        onNavigate={(p) => { sfx(sounds.blip); navigate(p); }}
        todayStr={todayStr}
        onSeeAll={() => navigate("/bizdev/briefs")}
      />

      {/* Awaiting me */}
      {(reviewTasks.length + reviewProjects.length) > 0 && (
        <CollapsedSection
          title="Awaiting me"
          count={`${reviewTasks.length + reviewProjects.length} review${(reviewTasks.length + reviewProjects.length) === 1 ? "" : "s"}`}
          countActive
          id="awaiting-me"
        >
          {reviewTasks.map((t) => (
            <TodayRow
              key={`rt-${t.task_id}`}
              kind="review"
              text={t.title || "Review task"}
              onClick={() => navigate(`/tasks/${t.task_id}`)}
              metadata={
                <>
                  {t.assignee_name && <><AvatarChip name={t.assignee_name} /><span>{t.assignee_name} submitted</span></>}
                  {t.updated_at && <><TodayRow.Sep /><span>{timeAgo(t.updated_at)}</span></>}
                </>
              }
            />
          ))}
          {reviewProjects.map((p) => (
            <TodayRow
              key={`rp-${p.project_id}`}
              kind="review"
              text={p.title || "Review project"}
              onClick={() => navigate(`/projects/${p.project_id}`)}
              metadata={<><span>Project review</span></>}
            />
          ))}
        </CollapsedSection>
      )}

      {/* This week */}
      {(upcomingThisWeek.length + upcomingBriefs.length + activeProjects.length) > 0 && (
        <CollapsedSection
          title="This week"
          count={`${upcomingThisWeek.length + upcomingBriefs.length} upcoming`}
          id="this-week"
        >
          {upcomingThisWeek.map((t) => (
            <TodayRow
              key={`up-${t.task_id}`}
              kind="task"
              text={t.title || "Task"}
              onClick={() => navigate(`/tasks/${t.task_id}`)}
              metadata={
                <>
                  <span>{formatDate(t.due_date)}</span>
                  {t.assignee_name && <><TodayRow.Sep /><AvatarChip name={t.assignee_name} /><span>{t.assignee_name}</span></>}
                </>
              }
            />
          ))}
          {upcomingBriefs.map((b) => (
            <TodayRow
              key={`ub-${b.brief_id}`}
              kind="brief"
              text={b.title || b.company_name || "Brief"}
              onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)}
              metadata={
                <>
                  {b.status && <TodayRow.Status status={b.status} />}
                  <TodayRow.Sep />
                  <span>Pitch {formatDate(b.pitch_date)}</span>
                </>
              }
            />
          ))}
        </CollapsedSection>
      )}

      {/* Projects live at /projects — only a tiny surface if user has active projects */}
      {activeProjects.length > 0 && (
        <CollapsedSection
          title="My projects"
          count={`${activeProjects.length} active`}
          id="my-projects"
        >
          {activeProjects.slice(0, 10).map((p) => (
            <TodayRow
              key={`prj-${p.project_id}`}
              kind="project"
              text={p.title || "Project"}
              onClick={() => navigate(`/projects/${p.project_id}`)}
              metadata={<>{p.status && <TodayRow.Status status={p.status} />}</>}
            />
          ))}
        </CollapsedSection>
      )}

      {/* MY TEAM — manager role overlay, collapsed by default */}
      {Array.isArray(teamPulse) && teamPulse.length > 0 && (
        <CollapsedSection
          title="My team"
          count={`${teamPulse.length} direct report${teamPulse.length === 1 ? "" : "s"}`}
          countActive={teamPulse.some((m) => m.overdue > 0)}
          id="my-team"
        >
          {teamPulse.map((m) => {
            const totalMeta = [];
            totalMeta.push(<span key="ip">{m.in_progress} in progress</span>);
            if (m.overdue > 0) {
              totalMeta.push(<TodayRow.Sep key="s1" />);
              totalMeta.push(<TodayRow.Overdue key="od">{m.overdue} overdue</TodayRow.Overdue>);
            }
            if (m.last_activity) {
              totalMeta.push(<TodayRow.Sep key="s2" />);
              totalMeta.push(<span key="la">last active {timeAgo(m.last_activity)}</span>);
            }
            return (
              <TodayRow
                key={`tp-${m.user_id}`}
                kind="team-member"
                text={m.name || m.user_id}
                onClick={() => navigate(`/team/${m.user_id}`)}
                metadata={<>{totalMeta}</>}
              />
            );
          })}
        </CollapsedSection>
      )}

      {/* ORG PULSE — admin / CEO overlay, collapsed by default */}
      {orgPulse && (
        <CollapsedSection
          title="Org pulse"
          count={`this week`}
          id="org-pulse"
        >
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
            padding: "8px 4px 4px",
          }}>
            <PulseStat label="Pipeline" value={`₹${orgPulse.pipeline_value_lakhs}L`} tone="blue" />
            <PulseStat label="Pitched (7d)" value={orgPulse.briefs_pitched_week} tone="violet" />
            <PulseStat label="Won (7d)" value={orgPulse.briefs_won_week} tone="green" />
            <PulseStat label="Active users" value={orgPulse.active_users} tone="neutral" />
            <PulseStat label="Overdue tasks" value={orgPulse.overdue_tasks_org} tone={orgPulse.overdue_tasks_org > 0 ? "red" : "neutral"} />
          </div>
        </CollapsedSection>
      )}

      {/* Story viewer for mobile IG-feed taps */}
      {storyUser && (
        <MobileStoryViewer
          userId={storyUser.user_id || storyUser.id}
          userName={storyUser.name || storyUser.full_name}
          userColor={`hsl(${(storyUser.name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 360}, 50%, 40%)`}
          onClose={() => setStoryUser(null)}
        />
      )}

      {/* Tiny shortcut hint (desktop only) */}
      {!isMobile && (
        <div
          style={{
            marginTop: 56,
            padding: "0 4px",
            color: "var(--t4, #3e3e3e)",
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          Shortcuts · <Kbd>J</Kbd>/<Kbd>K</Kbd> nav · <Kbd>Enter</Kbd> open · <Kbd>C</Kbd> complete · <Kbd>?</Kbd> help
        </div>
      )}
    </div>
  );
}

// ─────────── Helpers ───────────
function SectionHead({ title, count, rightHint }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      margin: "32px 0 16px",
      padding: "0 4px",
      userSelect: "none",
    }}>
      <span style={{
        fontFamily: "var(--font-sans)",
        fontSize: 19,
        fontWeight: 600,
        color: "var(--t1)",
        letterSpacing: "-0.015em",
      }}>
        {title}
      </span>
      {count && <span style={{ color: "var(--t3)", fontSize: 13, fontWeight: 500 }}>{count}</span>}
      <span style={{ flex: 1 }} />
      {rightHint && (
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          padding: "3px 9px",
          borderRadius: 999,
          background: "rgba(239,68,68,.14)",
          color: "var(--r)",
        }}>
          {rightHint}
        </span>
      )}
    </div>
  );
}

function Kbd({ children }) {
  return (
    <span style={{
      padding: "1px 6px",
      border: "1px solid var(--bdr)",
      borderRadius: 4,
      background: "var(--s0, var(--s1))",
      color: "var(--t3)",
      margin: "0 2px",
      fontFamily: "var(--font-mono)",
      fontSize: 11,
    }}>
      {children}
    </span>
  );
}

function daysDiff(fromStr, toStr) {
  try {
    const f = new Date(fromStr + "T00:00:00");
    const t = new Date(toStr + "T00:00:00");
    return Math.round((t - f) / 86400000);
  } catch { return 999; }
}

function formatDate(s) {
  if (!s) return "—";
  try {
    return new Date(s.slice(0, 10) + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch { return s; }
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

function PulseStat({ label, value, tone = "neutral" }) {
  const tones = {
    blue: { color: "#60a5fa", bg: "rgba(96,165,250,.1)" },
    violet: { color: "#a78bfa", bg: "rgba(167,139,250,.1)" },
    green: { color: "var(--g)", bg: "rgba(34,197,94,.1)" },
    red: { color: "var(--r)", bg: "rgba(239,68,68,.1)" },
    neutral: { color: "var(--t2)", bg: "var(--s1)" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div style={{
      background: t.bg,
      border: "1px solid var(--bdr)",
      borderRadius: 12,
      padding: "12px 14px",
    }}>
      <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 500, marginBottom: 4, letterSpacing: "0.02em" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: t.color, letterSpacing: "-0.01em", fontFamily: "var(--font-sans)" }}>
        {value}
      </div>
    </div>
  );
}

function describeTodayCount(tasksActive, priorities, briefsSoon) {
  const done = priorities.filter((p) => p && p.completed).length;
  const totalPriorities = priorities.filter((p) => p && p.text).length;
  const parts = [];
  if (totalPriorities) parts.push(`${done}/${totalPriorities} priorities`);
  if (tasksActive) parts.push(`${tasksActive} task${tasksActive === 1 ? "" : "s"}`);
  if (briefsSoon) parts.push(`${briefsSoon} brief${briefsSoon === 1 ? "" : "s"}`);
  if (parts.length === 0) return "";
  return parts.join(" · ");
}
